import React, { useState, useEffect } from 'react';

interface PDFViewerProps {
  pdfUrl: string;
  onClose: () => void;
  policyName: string;
  companyName: string;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ 
  pdfUrl, 
  onClose, 
  policyName, 
  companyName
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showIframe, setShowIframe] = useState(false);


  // 調試日誌
  console.log('PDFViewer 初始化:', {
    pdfUrl,
    policyName,
    companyName,
    hasUrl: !!pdfUrl,
    urlLength: pdfUrl?.length
  });

  // 添加超時機制
  useEffect(() => {
    if (loading && pdfUrl) {
      const timeout = setTimeout(() => {
        console.error('PDF載入超時:', { pdfUrl });
        setError('PDF載入超時，請檢查網絡連接或文件是否有效');
        setLoading(false);
      }, 5000); // 5秒超時，快速測試

      return () => clearTimeout(timeout);
    }
  }, [loading, pdfUrl]);

  // 當PDF URL改變時，重置載入狀態
  useEffect(() => {
    if (pdfUrl) {
      setLoading(true);
      setError(null);
    }
  }, [pdfUrl]);


  return (
    <div className="h-full flex flex-col rounded-lg overflow-hidden">
      {/* 標題欄 */}
      <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--card-shadow)', height: '65px' }}>
        <div>
          <h2 className="text-heading3" style={{ color: 'var(--charcoal)' }}>
            {policyName}
          </h2>
          <p className="text-caption" style={{ color: 'var(--light-gray)' }}>
            {companyName}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.open(pdfUrl, '_blank')}
            className="px-3 py-1 text-xs rounded border transition-colors duration-200 hover:bg-gray-100"
            style={{
              borderColor: 'var(--card-shadow)',
              color: 'var(--medium-gray)'
            }}
            title="在新窗口打開PDF"
          >
            新窗口
          </button>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg border transition-colors duration-200 hover:bg-gray-100"
            style={{
              borderColor: 'var(--card-shadow)',
              color: 'var(--medium-gray)'
            }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>


      {/* PDF內容區域 */}
      <div className="flex-1 overflow-auto p-4">
        <div className="flex justify-center">
          {loading && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-2" style={{ borderColor: 'var(--brand-teal)' }}></div>
                <p className="text-sm" style={{ color: 'var(--medium-gray)' }}>載入PDF中...</p>
              </div>
            </div>
          )}
          
          {error && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="text-red-500 mb-2">⚠️</div>
                <p className="text-sm text-red-600">{error}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--medium-gray)' }}>
                  請確認PDF文件存在且可訪問
                </p>
                <button
                  onClick={() => window.open(pdfUrl, '_blank')}
                  className="mt-3 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                >
                  在新窗口打開PDF
                </button>
              </div>
            </div>
          )}

          {!loading && !error && pdfUrl && (
            <div className="w-full h-full">
              <embed
                src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH&pagemode=none`}
                type="application/pdf"
                width="100%"
                height="100%"
                style={{ border: 'none', minHeight: '600px' }}
                onLoad={() => {
                  console.log('PDF embed載入成功');
                  setLoading(false);
                }}
                onError={() => {
                  console.log('PDF embed載入失敗，嘗試iframe');
                  setShowIframe(true);
                }}
              />
              
              {/* 如果embed失敗，顯示iframe */}
              {showIframe && (
                <iframe
                  src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH&pagemode=none`}
                  width="100%"
                  height="100%"
                  style={{ border: 'none', minHeight: '600px' }}
                  onLoad={(e) => {
                    console.log('PDF iframe載入成功:', e.target);
                    setLoading(false);
                  }}
                  onError={(e) => {
                    console.error('PDF iframe載入失敗:', e);
                    setError('無法載入PDF文件');
                    setLoading(false);
                  }}
                  title={`${policyName} - PDF預覽`}
                  sandbox="allow-same-origin allow-scripts"
                  allow="fullscreen"
                  loading="eager"
                />
              )}
            </div>
          )}

          {!loading && !error && !pdfUrl && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="text-red-500 mb-2">⚠️</div>
                <p className="text-sm text-red-600">PDF文件URL無效</p>
                <p className="text-xs mt-1" style={{ color: 'var(--medium-gray)' }}>
                  無法找到PDF文件
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
